// ── API Integration Wizard — API Client ──────────────────────────────────────
// Base: process.env.REACT_APP_MyAMP_Url + /api/v1/api-integrations
// Auth: Bearer token from localStorage('authToken')

import {
  BodyFormat, EndpointParam, IntegrationDetail, IntegrationSummary, TestDriveResult,
} from './apiIntegrations.types';

const BASE = `${process.env.REACT_APP_MyAMP_Url ?? ''}/api/v1/api-integrations`;

function token(): string {
  return localStorage.getItem('authToken') ?? '';
}

function headers(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: headers(), ...init });
  const json = await res.json().catch(() => ({}));
  // FastAPI's HTTPException responses carry `detail`; this router's own errors carry `error`.
  if (!res.ok || !json.success) throw new Error(json.error ?? json.detail ?? `Request failed (HTTP ${res.status})`);
  return json.data as T;
}

export type ParseEvent =
  | { type: 'progress'; raw: string; status: string }
  | { type: 'done'; integration: IntegrationDetail['integration']; endpoints: IntegrationDetail['endpoints']; parse_warnings?: string[] }
  | { type: 'error'; message: string };

// A real parse takes 30-100s — streamed so the wizard can show what's actually
// happening (the model's raw tool-call JSON arriving, plus a derived status
// line) instead of a static spinner. Mirrors claudeAgentApi.ts's streamChat.
// integrationId re-parses into that existing integration instead of creating
// a new draft. Aborting via `signal` cancels the parse; nothing is saved.
export async function parseDocumentationStream(
  rawDocs: string, nameHint: string | undefined, onEvent: (e: ParseEvent) => void,
  signal?: AbortSignal, integrationId?: number,
): Promise<void> {
  const res = await fetch(`${BASE}/parse`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ raw_docs: rawDocs, name_hint: nameHint, integration_id: integrationId }),
    signal,
  });
  if (!res.ok || !res.body) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error ?? json.detail ?? `Parsing couldn't start (HTTP ${res.status}).`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const raw = trimmed.slice(5).trim();
      if (!raw) continue;
      try { onEvent(JSON.parse(raw) as ParseEvent); } catch { /* skip malformed */ }
    }
  }
}

export interface ExtractedDocs {
  text: string; pages: number; filename: string; chars: number; max_chars: number;
}

// "Import a PDF": the file's text comes back for the docs box - nothing is saved.
export async function extractDocsFromPdf(file: File): Promise<ExtractedDocs> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/extract-docs`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: form });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) throw new Error(json.error ?? json.detail ?? `Couldn't import that PDF (HTTP ${res.status}).`);
  return json.data as ExtractedDocs;
}

export function listIntegrations(): Promise<IntegrationSummary[]> {
  return api('');
}

export function getIntegration(id: number): Promise<IntegrationDetail> {
  return api(`/${id}`);
}

export function updateIntegration(id: number, fields: { name?: string; description?: string; base_url?: string; icon?: string; category?: string }) {
  return api(`/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

export function deleteIntegration(id: number) {
  return api(`/${id}`, { method: 'DELETE' });
}

export function updateAuth(id: number, body: AuthSettings) {
  return api(`/${id}/auth`, { method: 'PATCH', body: JSON.stringify(body) });
}

export interface EndpointFormValue {
  name: string; summary?: string; description: string; http_method: string; path_template: string;
  returns_summary?: string | null; body_format?: BodyFormat; is_idempotent: boolean; timeout_seconds: number;
  params: EndpointParam[];
}

export function createEndpoint(integrationId: number, endpoint: EndpointFormValue) {
  return api<{ endpoint_id: number }>(`/${integrationId}/endpoints`, {
    method: 'POST', body: JSON.stringify(endpoint),
  });
}

export function updateEndpoint(integrationId: number, endpointId: number, endpoint: EndpointFormValue) {
  return api(`/${integrationId}/endpoints/${endpointId}`, {
    method: 'PATCH', body: JSON.stringify(endpoint),
  });
}

export function deleteEndpoint(integrationId: number, endpointId: number) {
  return api(`/${integrationId}/endpoints/${endpointId}`, { method: 'DELETE' });
}

export function testDrive(integrationId: number, endpointId: number, paramValues: Record<string, unknown>): Promise<TestDriveResult> {
  return api(`/${integrationId}/endpoints/${endpointId}/test-drive`, {
    method: 'POST', body: JSON.stringify({ param_values: paramValues }),
  });
}

export interface PublishResult {
  integration: IntegrationDetail['integration'];
  warnings: string[];
  // The listing after publishing: 2 = Global. Global is set by an approved review,
  // so publishing as Global comes back with publish_status 'pending'.
  visibility?: number | null;
  publish_status?: string | null;
  // Saved keys removed because who supplies the key changed.
  keys_removed?: number;
}

export interface AuthSettings {
  auth_type: string; location?: string | null; param_name?: string | null; secret?: string;
}

export type InstallerKeyScope = 'agency' | 'per_user';

export interface PublishRequest {
  // 'global' submits it to the Review Queue; an approval makes it Global.
  visibility: 'just_me' | 'my_agency' | 'global';
  // Who supplies the API key when someone installs it.
  installer_key_scope?: InstallerKeyScope;
  name?: string;
  description?: string;
  icon?: string;
  category?: string;
  skills?: string;
  base_url?: string;
  auth?: AuthSettings;
}

// Saves everything on the publish step in the same call, then marks the
// integration ready and installs it for its creator.
export function publishIntegration(id: number, body: PublishRequest): Promise<PublishResult> {
  return api(`/${id}/publish`, { method: 'POST', body: JSON.stringify(body) });
}

// ── API keys ─────────────────────────────────────────────────────────────────
// Nobody's calls use someone else's key. The key typed on the Connect step is
// only the creator's own; everyone who installs an integration adds theirs - one
// per agency (set by an Agency Admin) or one per person, as the creator chose.
// The key is never returned - only whether it's set, whose it is, and its last 4.

export type KeyTarget = 'me' | 'agency';

export interface InstallerCredentialStatus {
  // false: the API takes no key.
  required: boolean;
  scope?: InstallerKeyScope;
  set?: boolean;
  // Whose key this person's calls use: their own, or their agency's.
  source?: 'you' | 'agency' | null;
  last4?: string | null;
  updated_at?: string | null;
  // Which key they can change here - null when their Agency Admin manages it.
  target?: KeyTarget | null;
  can_edit?: boolean;
  is_agency_owner?: boolean;
  // Agency Admins on one-key-per-agency integrations only: the agency's key, which
  // they can always manage, and their own saved key (which an agency install can
  // offer to reuse as the agency's).
  agency_key?: { set: boolean; last4?: string | null; updated_at?: string | null };
  own_key?: { set: boolean; last4?: string | null };
}

export function getMyCredential(integrationId: number): Promise<InstallerCredentialStatus> {
  return api(`/${integrationId}/my-credential`);
}

export function saveMyCredential(integrationId: number, secret: string, target?: KeyTarget): Promise<InstallerCredentialStatus> {
  return api(`/${integrationId}/my-credential`, { method: 'PUT', body: JSON.stringify({ secret, target }) });
}

// An Agency Admin's choice to use the key they saved for themselves as the agency's.
// The server copies it - the key itself never reaches the browser.
export function copyMyKeyToAgency(integrationId: number): Promise<InstallerCredentialStatus> {
  return api(`/${integrationId}/my-credential`, { method: 'PUT', body: JSON.stringify({ target: 'agency', copy_from_mine: true }) });
}

export interface KeyScopeImpact {
  keys_removed: number;
  // People who have it through their agency and would be left without the key they need.
  people_needing_key: number;
}

export function getKeyScopeImpact(integrationId: number, scope: InstallerKeyScope): Promise<KeyScopeImpact> {
  return api(`/${integrationId}/key-scope-impact?scope=${scope}`);
}

export interface InstalledCredentialState {
  integration_id: number; scope: InstallerKeyScope; set: boolean; can_edit: boolean;
}

// Installed integrations that need an API key - drives the "needs a key" badge.
export function getCredentialStatus(): Promise<InstalledCredentialState[]> {
  return api('/credential-status');
}
